import { defineComponent as _defineComponent } from 'vue'
import {
	ModuleContentType,
	ModuleTemplate,
	ObjectType,
	Platform,
	PopularTitlesSorting,
	PopularityRankTypes,
	StreamingChartObjectType,
} from '@/@types/graphql-types';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { SnowplowModuleContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import Provider from '@/interfaces/provider';
import { PopularTabType } from '@/interfaces/titles';
import { computed } from 'vue';
import Top10HorizontalTitleListGraphql from './Top10HorizontalTitleListGraphql.vue';
import { getSelectedTab } from './helper';
import {
	GetProviderTop10TitlesDocument,
	GetProviderTop10TitlesQuery,
	GetProviderTop10TitlesQueryVariables,
} from './queries/GetProviderTop10Titles.query';
import {
	GetProviderTop10TitlesFallbackDocument,
	GetProviderTop10TitlesFallbackQuery,
	GetProviderTop10TitlesFallbackQueryVariables,
} from './queries/GetProviderTop10TitlesFallback.query';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import type { HorizontalTitleListItem } from '@/components/timeline/HorizontalTitleListGraphql.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Top10Titles',
  props: {
    provider: null,
    selectedTab: null,
    showTitle: { type: Boolean, default: true },
    showMobileTitle: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { provider: Provider, selectedTab: PopularTabType, showTitle: boolean, showMobileTitle: boolean };



const { language, country } = useLanguageStore();
const { activeRoute } = useRoutingStore();
const { t } = useI18n();
useProvideClickoutPlacement('top_10');

const top10TitlesObjectTypes = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.MOVIE:
			return [ObjectType.Movie];

		case PopularTabType.SHOW:
			return [ObjectType.Show];

		case PopularTabType.ALL:
			return [ObjectType.Show, ObjectType.Movie];

		default:
			return [];
	}
});

const titlesToDisplay = computed(() => (titleNodes.value?.length !== 10 ? fallbackTitleNodes.value : titleNodes.value));

const titleNodes = computed(() => {
	if (props.selectedTab === PopularTabType.MOVIE) {
		return top10ProviderMovieTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	if (props.selectedTab === PopularTabType.SHOW) {
		return top10ProviderShowTitles.result.value?.streamingCharts.edges?.map((el: { node: any }) => el.node);
	}

	return top10MixedTitles.value?.map((el: { node: any }) => el.node);
});

const fallbackTitleNodes = computed(() => {
	return top10FallbackTitles.result.value?.popularTitles.edges
		?.map((el: { node: any }) => el.node)
		.sort(
			(
				a: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } },
				b: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } }
			) => {
				const position_a = a.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				const position_b = b.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
				return position_a - position_b;
			}
		);
});

const top10MixedTitles = computed(() => {
	const mixedTitles = top10ProviderMovieTitles?.result.value?.streamingCharts.edges?.concat(
		top10ProviderShowTitles?.result.value?.streamingCharts.edges || []
	);

	const sortedMixedTitles = mixedTitles?.sort((a: any, b: any) => {
		if (a?.streamingChartInfo?.rank && b?.streamingChartInfo?.rank) {
			const rankScoreA = a?.streamingChartInfo?.rank * 0.99;
			const rankScoreB = b?.streamingChartInfo?.rank * 0.99;
			const daysInTop10ScoreA = a?.streamingChartInfo?.daysInTop10 * 0.01;
			const daysInTop10ScoreB = b?.streamingChartInfo?.daysInTop10 * 0.01;

			const scoreA = rankScoreA + daysInTop10ScoreA;
			const scoreB = rankScoreB + daysInTop10ScoreB;

			a = {
				...a,
				score: scoreA,
			};

			b = {
				...b,
				score: scoreB,
			};

			return a.score - b.score;
		}
		return 0;
	});

	return sortedMixedTitles?.slice(0, 10);
});

const titleText = computed(() => {
	switch (props.selectedTab) {
		case PopularTabType.ALL:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TITLES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.MOVIE:
			return t('WEBAPP_PROVIDER_PAGE_TOP_MOVIES', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		case PopularTabType.SHOW:
			return t('WEBAPP_PROVIDER_PAGE_TOP_TV_SHOWS', {
				provider: props.provider.clearName,
				number: titlesToDisplay.value?.length ?? 10,
			}).toString();
		default:
			return '';
	}
});

const top10ProviderMovieTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Movie,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const top10ProviderShowTitles = useQuery<GetProviderTop10TitlesQuery, GetProviderTop10TitlesQueryVariables>(
	GetProviderTop10TitlesDocument,
	() => ({
		after: '',
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
		},
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			objectType: StreamingChartObjectType.Show,
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			nextTitles: 0,
			previousTitles: 0,
			packages: [props.provider.shortName],
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const top10FallbackTitles = useQuery<GetProviderTop10TitlesFallbackQuery, GetProviderTop10TitlesFallbackQueryVariables>(
	GetProviderTop10TitlesFallbackDocument,
	() => ({
		after: '',
		popularTitlesFilter: {
			packages: [props.provider.shortName],
			objectTypes: top10TitlesObjectTypes.value,
		},
		watchNowFilter: {
			packages: [props.provider.shortName],
		},
		offerFilter: {
			packages: [props.provider.shortName],
			bestOnly: true,
			preAffiliate: true,
		},
		popularTitlesSortBy: PopularTitlesSorting.Trending,
		language: language.value,
		country: country.value,
		first: 10,
		platform: Platform.Web,
		streamingChartsFilter: {
			category: PopularityRankTypes.WeeklyPopularitySameContentType,
			objectType:
				top10TitlesObjectTypes.value.length === 1 ? (top10TitlesObjectTypes.value[0] as any) : undefined,
			nextTitles: 0,
			previousTitles: 0,
		},
	}),
	{ fetchPolicy: 'cache-and-network' }
);

const isLoading = computed(
	() =>
		top10ProviderMovieTitles.loading.value ||
		top10ProviderShowTitles.loading.value ||
		top10FallbackTitles.loading.value
);

function trackTitleClick({ index, title }: { index: number; title: HorizontalTitleListItem }) {
	const selectedTab = getSelectedTab(activeRoute?.value.name);

	const moduleContext = getModuleContext({
		technicalName: 'top-10-titles',
		anchor: '',
		contentType: ModuleContentType.TitlesPerPackage,
	});

	const titleContext = new SnowplowTitleContextGraphql(title.objectId, title.objectType);

	const contexts = [moduleContext, titleContext];

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: 'top-10-titles',
			property: selectedTab,
			value: index,
		},
		contexts
	);
}

function getModuleContext(moduleTemplate: ModuleTemplate) {
	return new SnowplowModuleContext('', -1, moduleTemplate.technicalName, '', moduleTemplate.anchor || undefined);
}

return { __sfc: true,props, language, country, activeRoute, t, top10TitlesObjectTypes, titlesToDisplay, titleNodes, fallbackTitleNodes, top10MixedTitles, titleText, top10ProviderMovieTitles, top10ProviderShowTitles, top10FallbackTitles, isLoading, trackTitleClick, getModuleContext, Top10HorizontalTitleListGraphql }
}

})